/**
 * Análise de áudio (usada pelo AnalysisEngine).
 * Funções puras sobre Float32Array — testáveis sem navegador.
 */
import { clamp, gainToDb, median } from '../utils/misc.js';

export function peak(data: Float32Array): number {
  let p = 0;
  for (let i = 0; i < data.length; i++) {
    const a = Math.abs(data[i]);
    if (a > p) p = a;
  }
  return p;
}

export function rms(data: Float32Array): number {
  if (!data.length) return 0;
  let s = 0;
  for (let i = 0; i < data.length; i++) s += data[i] * data[i];
  return Math.sqrt(s / data.length);
}

/** RMS em janelas (para medidores e análise de dinâmica). */
export function rmsWindows(
  data: Float32Array,
  sampleRate: number,
  windowMs = 100,
  hopMs = 50
): { times: number[]; values: number[] } {
  const w = Math.max(1, Math.round((sampleRate * windowMs) / 1000));
  const h = Math.max(1, Math.round((sampleRate * hopMs) / 1000));
  const times: number[] = [];
  const values: number[] = [];
  for (let i = 0; i + w <= data.length; i += h) {
    let s = 0;
    for (let j = i; j < i + w; j++) s += data[j] * data[j];
    times.push((i + w / 2) / sampleRate);
    values.push(Math.sqrt(s / w));
  }
  return { times, values };
}

export function crestFactor(data: Float32Array): number {
  const r = rms(data);
  if (r <= 0) return 0;
  return gainToDb(peak(data)) - gainToDb(r);
}

/** Peaks min/max por bloco de pixels — para desenhar waveform rapidamente. */
export function computePeaks(
  data: Float32Array,
  buckets: number
): { min: Float32Array; max: Float32Array } {
  const n = Math.max(1, buckets | 0);
  const min = new Float32Array(n);
  const max = new Float32Array(n);
  if (!data.length) return { min, max };
  const per = data.length / n;
  for (let b = 0; b < n; b++) {
    const start = Math.floor(b * per);
    const end = Math.min(data.length, Math.floor((b + 1) * per));
    let lo = Infinity, hi = -Infinity;
    for (let i = start; i < end; i++) {
      const v = data[i];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    if (!isFinite(lo)) lo = 0;
    if (!isFinite(hi)) hi = 0;
    min[b] = lo;
    max[b] = hi;
  }
  return { min, max };
}

export interface SilenceRegion { start: number; end: number; peak: number }

/**
 * Detecta regiões silenciosas (pausas, respirações longas).
 * Não classifica respiração como "erro" — só reporta.
 */
export function findSilence(
  data: Float32Array,
  sampleRate: number,
  thresholdDb = -50,
  minMs = 250
): SilenceRegion[] {
  const thr = Math.pow(10, thresholdDb / 20);
  const win = Math.max(1, Math.round((sampleRate * 20) / 1000));
  const regions: SilenceRegion[] = [];
  let start = -1;
  let maxP = 0;
  for (let i = 0; i < data.length; i += win) {
    const end = Math.min(data.length, i + win);
    let p = 0;
    for (let j = i; j < end; j++) p = Math.max(p, Math.abs(data[j]));
    if (p < thr) {
      if (start < 0) { start = i; maxP = p; } else maxP = Math.max(maxP, p);
    } else if (start >= 0) {
      const dur = ((i - start) / sampleRate) * 1000;
      if (dur >= minMs) {
        regions.push({ start: start / sampleRate, end: i / sampleRate, peak: maxP });
      }
      start = -1;
    }
  }
  if (start >= 0) {
    const dur = ((data.length - start) / sampleRate) * 1000;
    if (dur >= minMs) regions.push({ start: start / sampleRate, end: data.length / sampleRate, peak: maxP });
  }
  return regions;
}

export interface ClippingRegion { start: number; end: number; peak: number; samples: number }

/** Regiões com clipping (|x| >= 0.999) — reportadas com tempo para o coach. */
export function findClipping(
  data: Float32Array,
  sampleRate: number,
  threshold = 0.995,
  minSamples = 3
): ClippingRegion[] {
  const regions: ClippingRegion[] = [];
  let start = -1;
  let count = 0;
  let p = 0;
  for (let i = 0; i < data.length; i++) {
    const a = Math.abs(data[i]);
    if (a >= threshold) {
      if (start < 0) { start = i; count = 0; p = a; }
      count++;
      p = Math.max(p, a);
    } else if (start >= 0) {
      if (count >= minSamples) {
        regions.push({ start: start / sampleRate, end: i / sampleRate, peak: p, samples: count });
      }
      start = -1;
    }
  }
  if (start >= 0 && count >= minSamples) {
    regions.push({ start: start / sampleRate, end: data.length / sampleRate, peak: p, samples: count });
  }
  return regions;
}

/**
 * Onsets por fluxo espectral simplificado (diferença de energia por banda).
 * Usado por auto-sync, análise de ritmo e detecção de ataque inicial.
 */
export function detectOnsets(
  data: Float32Array,
  sampleRate: number,
  opts: { minIntervalMs?: number; sensitivity?: number; max?: number } = {}
): { times: number[]; strengths: number[] } {
  const minIntervalMs = opts.minIntervalMs ?? 80;
  const sensitivity = clamp(opts.sensitivity ?? 1.6, 1.05, 4);
  const hop = Math.max(64, Math.round((sampleRate * 10) / 1000)); // ~10 ms
  const bands = 16;
  const env: number[] = [];
  const prev = new Float32Array(bands);
  for (let i = 0; i + hop <= data.length; i += hop) {
    let flux = 0;
    const per = Math.floor(hop / bands) || 1;
    for (let b = 0; b < bands; b++) {
      let e = 0;
      const s = i + b * per;
      const e2 = Math.min(i + hop, s + per);
      for (let j = s; j < e2; j++) e += data[j] * data[j];
      e = Math.sqrt(e / Math.max(1, e2 - s));
      const d = e - prev[b];
      if (d > 0) flux += d;
      prev[b] = e;
    }
    env.push(flux);
  }
  if (env.length < 8) return { times: [], strengths: [] };
  const med = median(env);
  const mean = env.reduce((a, b) => a + b, 0) / env.length;
  const thr = Math.max(med * sensitivity, mean * (sensitivity * 0.75), 1e-6);
  const minGap = Math.max(1, Math.round(minIntervalMs / 10));
  const times: number[] = [];
  const strengths: number[] = [];
  for (let i = 2; i < env.length - 2; i++) {
    if (
      env[i] > thr &&
      env[i] >= env[i - 1] &&
      env[i] > env[i + 1] &&
      env[i] > env[i - 2] &&
      env[i] >= env[i + 2]
    ) {
      if (times.length && i - (times[times.length - 1] * 100) / 10 < minGap) continue;
      times.push((i * hop) / sampleRate);
      strengths.push(env[i]);
    }
  }
  if (opts.max && times.length > opts.max) {
    const idx = strengths
      .map((s, i) => ({ s, i }))
      .sort((a, b) => b.s - a.s)
      .slice(0, opts.max)
      .map((x) => x.i)
      .sort((a, b) => a - b);
    return { times: idx.map((i) => times[i]), strengths: idx.map((i) => strengths[i]) };
  }
  return { times, strengths };
}

/** Primeiro ataque audível acima de um limiar (usado no auto-sync por palma). */
export function firstTransient(
  data: Float32Array,
  sampleRate: number,
  thresholdDb = -40
): number | null {
  const thr = Math.pow(10, thresholdDb / 20);
  const win = Math.max(1, Math.round((sampleRate * 5) / 1000));
  for (let i = 0; i + win <= data.length; i += win) {
    let p = 0;
    for (let j = i; j < i + win; j++) p = Math.max(p, Math.abs(data[j]));
    if (p >= thr) {
      // refina até o sample
      for (let j = Math.max(0, i - win); j < i + win && j < data.length; j++) {
        if (Math.abs(data[j]) >= thr) return j / sampleRate;
      }
      return i / sampleRate;
    }
  }
  return null;
}

/**
 * Loudness aproximado (estilo ITU-R BS.1770 sem gating completo).
 * Retorna LUFS aproximado — usamos para balancear voz x instrumental e
 * para normalização opcional. Não substitui medição profissional.
 */
export function loudnessLKFS(channels: Float32Array[], sampleRate: number): number {
  const pre = kWeightFilters(sampleRate);
  let sum = 0;
  let n = 0;
  for (const ch of channels) {
    const f = applyBiquadCascade(ch, pre);
    for (let i = 0; i < f.length; i++) sum += f[i] * f[i];
    n += f.length;
  }
  if (!n) return -Infinity;
  const mean = sum / n;
  if (mean <= 0) return -Infinity;
  return -0.691 + 10 * Math.log10(mean);
}

interface Biquad { b0: number; b1: number; b2: number; a1: number; a2: number }

function kWeightFilters(fs: number): Biquad[] {
  // Filtros do padrão BS.1770 (high-shelf + high-pass), coeficientes
  // derivados das fórmulas RBJ para o sample rate em uso.
  const shelf = highShelf(fs, 1681.974450955533, 0.7071752369554196, 3.999843853973347);
  const hp = highPass(fs, 38.13547087602444, 0.5003270373238773);
  return [shelf, hp];
}

function highPass(fs: number, f0: number, Q: number): Biquad {
  const w0 = (2 * Math.PI * f0) / fs;
  const cw = Math.cos(w0), sw = Math.sin(w0);
  const alpha = sw / (2 * Q);
  const b0 = (1 + cw) / 2, b1 = -(1 + cw), b2 = (1 + cw) / 2;
  const a0 = 1 + alpha, a1 = -2 * cw, a2 = 1 - alpha;
  return { b0: b0 / a0, b1: b1 / a0, b2: b2 / a0, a1: a1 / a0, a2: a2 / a0 };
}

function highShelf(fs: number, f0: number, S: number, gainDb: number): Biquad {
  const A = Math.pow(10, gainDb / 40);
  const w0 = (2 * Math.PI * f0) / fs;
  const cw = Math.cos(w0), sw = Math.sin(w0);
  const alpha = (sw / 2) * Math.sqrt((A + 1 / A) * (1 / S - 1) + 2);
  const sqA = Math.sqrt(A);
  const b0 = A * ((A + 1) + (A - 1) * cw + 2 * sqA * alpha);
  const b1 = -2 * A * ((A - 1) + (A + 1) * cw);
  const b2 = A * ((A + 1) + (A - 1) * cw - 2 * sqA * alpha);
  const a0 = (A + 1) - (A - 1) * cw + 2 * sqA * alpha;
  const a1 = 2 * ((A - 1) - (A + 1) * cw);
  const a2 = (A + 1) - (A - 1) * cw - 2 * sqA * alpha;
  return { b0: b0 / a0, b1: b1 / a0, b2: b2 / a0, a1: a1 / a0, a2: a2 / a0 };
}

export function applyBiquadCascade(data: Float32Array, filters: Biquad[]): Float32Array {
  let out = data;
  for (const f of filters) {
    const next = new Float32Array(out.length);
    let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
    for (let i = 0; i < out.length; i++) {
      const x0 = out[i];
      const y0 = f.b0 * x0 + f.b1 * x1 + f.b2 * x2 - f.a1 * y1 - f.a2 * y2;
      next[i] = y0;
      x2 = x1; x1 = x0; y2 = y1; y1 = y0;
    }
    out = next;
  }
  return out;
}

/**
 * Encontra o ganho que leva o pico a `targetDb` sem passar de 0 dBFS.
 * Usado em "ajustar volume automaticamente" e normalização de exportação.
 */
export function autoGain(
  data: Float32Array,
  targetPeakDb = -1,
  maxBoostDb = 18,
  maxCutDb = -24
): { gain: number; beforeDb: number; afterDb: number; clipped: boolean } {
  const p = peak(data);
  const beforeDb = gainToDb(p);
  if (!isFinite(beforeDb)) return { gain: 1, beforeDb: -Infinity, afterDb: -Infinity, clipped: false };
  let target = clamp(targetPeakDb, -40, 0);
  // Se o sinal já clipa, o alvo é apenas tirar do vermelho.
  if (p >= 0.999) target = Math.min(target, -1);
  let gain = Math.pow(10, (target - beforeDb) / 20);
  const maxBoost = Math.pow(10, maxBoostDb / 20);
  const maxCut = Math.pow(10, maxCutDb / 20);
  gain = clamp(gain, maxCut, maxBoost);
  return { gain, beforeDb, afterDb: beforeDb + 20 * Math.log10(gain), clipped: p >= 0.999 };
}

/** Aplica gain com soft-clip no topo para nunca devolver amostra > 1. */
export function applyGainSoft(data: Float32Array, gain: number, ceiling = 0.999): Float32Array {
  const out = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) {
    let v = data[i] * gain;
    if (v > ceiling) v = ceiling + Math.tanh(v - ceiling) * (1 - ceiling);
    else if (v < -ceiling) v = -ceiling + Math.tanh(v + ceiling) * (1 - ceiling);
    out[i] = v;
  }
  return out;
}

/** Fade in/out aplicados sem destruir o original. */
export function applyFades(
  data: Float32Array,
  sampleRate: number,
  fadeInMs: number,
  fadeOutMs: number
): Float32Array {
  const out = Float32Array.from(data);
  const fi = Math.round((fadeInMs / 1000) * sampleRate);
  const fo = Math.round((fadeOutMs / 1000) * sampleRate);
  for (let i = 0; i < Math.min(fi, out.length); i++) out[i] *= i / fi;
  for (let i = 0; i < Math.min(fo, out.length); i++) {
    const idx = out.length - 1 - i;
    out[idx] *= i / fo;
  }
  return out;
}

/** Converte canal único em estéreo com pan (lei constante de potência). */
export function panMonoToStereo(
  data: Float32Array,
  pan: number
): { left: Float32Array; right: Float32Array } {
  const p = clamp(pan, -1, 1);
  const angle = ((p + 1) / 2) * (Math.PI / 2);
  const gl = Math.cos(angle);
  const gr = Math.sin(angle);
  const left = new Float32Array(data.length);
  const right = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) {
    left[i] = data[i] * gl;
    right[i] = data[i] * gr;
  }
  return { left, right };
}

/** Mistura dois buffers estéreo de tamanhos diferentes (soma com ganho). */
export function mixBuffers(
  target: { left: Float32Array; right: Float32Array },
  source: { left: Float32Array; right: Float32Array },
  offsetSamples: number,
  gain = 1
): void {
  const n = source.left.length;
  for (let i = 0; i < n; i++) {
    const t = i + offsetSamples;
    if (t < 0 || t >= target.left.length) continue;
    target.left[t] += source.left[i] * gain;
    target.right[t] += source.right[i] * gain;
  }
}
